import { Injectable } from '@angular/core';
import { CovanceGridColumn } from '../covance-ag-grid/covance-grid-column';
import { MulticellAgRendererComponent, MulticellItem, MulticellParams, MulticellTooltip } from '../covance-ag-grid/renderers/multicell-ag-renderer.component';
import { isEmpty } from 'lodash';
import { ActionsMenuAgRendererComponent } from '../covance-ag-grid/renderers/actions-menu-ag-renderer-base';
import { comparators } from '../core';
import { UserPermissions } from '../user-permissions/user-permissions';
import { CategoryNaturalKey } from '../documents/models/category';
import { AgeRendererComponent } from '../covance-ag-grid/renderers/age-renderer/age-renderer.component';
import { LinkParams, LinkAgRendererComponent } from '../covance-ag-grid';
import { SubjectTitleRenderer } from '../renderer/subject-title-renderer';

export interface ActionsMenuColumn<TEntity> extends CovanceGridColumn {
    userPermissions?: UserPermissions;
    categoryId?: CategoryNaturalKey;
    userId?: number;
    cellRendererFramework: new (...args: any[]) => ActionsMenuAgRendererComponent<TEntity>;
}

interface MultiLineStringColumnParams<T> {
    title: string;
    field: string;
    stringsValueGetter: (entity: T) => string[];
    tooltipGetter: (s: string) => MulticellTooltip[];
    useFilter?: boolean;
}

@Injectable()
export class GridColumns {

    public multiLineStringColumn<T>(parameters: MultiLineStringColumnParams<T>): CovanceGridColumn {
        const { title, field, stringsValueGetter, tooltipGetter, useFilter } = parameters;

        let colDef: CovanceGridColumn = {
            headerName: title,
            field: field,
            cellRendererFramework: MulticellAgRendererComponent,
            getLineCount: (entity: T) => {
                const strings = stringsValueGetter(entity);
                return strings ? strings.length : 0;
            },
            getRowHeight: (entity: T) => {
                //the value of lineHeight should match the one in grid.scss
                const lineHeight = 23;
                const strings = stringsValueGetter(entity);

                return (strings ? strings.length : 0) * lineHeight;
            },
            valueGetter: (params: { data: T }) => {
                const dataStrings = stringsValueGetter(params.data);
                if (isEmpty(dataStrings)) {
                    return null;
                }

                const items = dataStrings.map(role => ({
                    displayValue: role,
                    tooltips: tooltipGetter(role) //add tooltip
                } as MulticellItem));

                return {
                    items: items,
                    toString: () => dataStrings.join('\n')
                } as MulticellParams;
            },
            comparator: (a: MulticellParams, b: MulticellParams) => {
                return comparators.stringComparator(a.toString(), b.toString());
            }
        };

        if (useFilter) {
            colDef.filterValueGetter = (params: { data: T }) => {
                return stringsValueGetter(params.data);
            };
            colDef.filter = 'agSetColumnFilter';
        }

        return colDef;
    }

    public actionsMenuColumn<TEntity>(properties: ActionsMenuColumn<TEntity>): ActionsMenuColumn<TEntity> {
        return {
            ...properties,
            headerName: 'ActionsMenu',
            suppressSorting: true,
            noExport: true,
            suppressMenu: true,
            suppressFilter: true,
            maxWidth: 70,
            cellStyle: { overflow: 'visible', display: 'flex', justifyContent: 'flex-end' },
            headerValueGetter: () => ''
        };
    }

    public accessionNumberColumn<TData>(
        buildRouterLink: (params: AccessionNumberRendererParams<TData>) => string | any[],
        accessionNumbersAccessor: (data: TData) => string[],
    ): CovanceGridColumn {
        return {
            headerName: 'Accession',
            field: 'accessionNumber',
            width: 95,
            valueGetter: (params: AccessionNumberRendererParams<TData>) => {
                if (!params.data) {
                    return null;
                }

                const accessionNumbers: string[] = accessionNumbersAccessor(params.data);

                const textedAccessionNumbers = accessionNumbers.join(', ');
                const serializedAccessionNumbers = JSON.stringify(accessionNumbers);

                const routerLink: LinkParams = {
                    routerLink: buildRouterLink(params),
                    queryParams: { accessionNumber: serializedAccessionNumbers },
                    text: textedAccessionNumbers,
                    toString: () => textedAccessionNumbers
                };

                return routerLink;
            },
            comparator: comparators.linkParamsComparator,
            cellRendererFramework: LinkAgRendererComponent,
        };
    }

    public subjectColumn<TData extends DataWithSubject>(
        buildRouterLink: (params: SubjectRendererParams<TData>) => string | any[],
        titleRenderer: (data: TData) => string = data => {
            return data.subjectId ? SubjectTitleRenderer.renderTitle({
                data: {
                    subjectNumber: data.subjectNumber,
                    initials: data.subjectInitials,
                    screeningNumber: data.screeningNumber,
                    zcSubjectId: data.zcSubjectId
                }
            }) : '';
        }
    ): CovanceGridColumn {
        return {
            headerName: 'Subject',
            field: 'subjectId',
            width: 140,
            valueGetter: (params: SubjectRendererParams<TData>) => {
                if (!params.data) {
                    return null;
                }

                const subjectTitle = titleRenderer(params.data);

                const linkParams: LinkParams = {
                    routerLink: params.data.zcSubjectId
                        ? buildRouterLink(params)
                        : [],
                    text: subjectTitle,
                    toString: () => subjectTitle
                };

                return linkParams;
            },
            comparator: comparators.linkParamsComparator,
            cellRendererFramework: LinkAgRendererComponent
        };
    }
}

interface AccessionNumberRendererParams<TData> {
    data: TData;
}

interface DataWithSubject {
    zcSubjectId: string;
    subjectId: number;
    subjectNumber: string;
    subjectInitials: string;
    screeningNumber: string;
}

interface SubjectRendererParams<TData> {
    data: TData;
}
